import React, { useCallback, useEffect, useRef, useState } from "react";
import Reveal from "./ui/Reveal";
import SectionLabel from "./ui/SectionLabel";

const DURATION = 7000; // tempo de cada passo no modo automático

const STEPS = [
  {
    id: "agenda",
    label: "Agenda do dia",
    title: "O dia inteiro numa tela só",
    text: "Banhos, tosas e taxi-dog em uma linha do tempo por horário. Os totais do dia ficam sempre no topo, e cada card mostra o status do serviço em tempo real.",
    image: "/tela_principal.png",
    alt: "Agenda diária do PawSuite com serviços organizados por horário",
    bullets: [
      "Totais de banhos, tosas e faturamento no topo",
      "Status visual: agendado, em andamento, finalizado",
      "Navegação rápida entre dias e semanas",
    ],
    accent: "tealVitality",
  },
  {
    id: "pets",
    label: "Ficha dos pets",
    title: "Cada pet com a história completa",
    text: "Raça, porte, cor, observações e tutor responsável — tudo à mão no atendimento. A ficha vira a base do prontuário clínico e dos alertas de vacina.",
    image: "/tela_pets.png",
    alt: "Fichas de pets no PawSuite com dados do tutor, porte e status",
    bullets: [
      "Cadastro com foto, raça e porte",
      "Observações de comportamento e cuidados",
      "Vínculo direto com o tutor e o histórico",
    ],
    accent: "pawOrange",
  },
  {
    id: "admin",
    label: "Painel do gestor",
    title: "Os números do negócio, sem planilha",
    text: "Relatórios, permissões de equipe e histórico de pagamentos em um painel pensado para quem decide. Exporte em PDF com um clique.",
    image: "/tela_admin.png",
    alt: "Painel administrativo do PawSuite com relatórios e gestão",
    bullets: [
      "Relatórios de desempenho em PDF",
      "Permissões por função na equipe",
      "Histórico completo de transações",
    ],
    accent: "tealVitality",
  },
];

const ProductTour = () => {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  // Só roda o carrossel enquanto a seção está visível — nada de animar fora da tela
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const observer = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Avanço automático com barra de progresso
  useEffect(() => {
    if (!playing) {
      setProgress(0);
      return;
    }

    startRef.current = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startRef.current) / DURATION, 1);
      setProgress(p);
      if (p >= 1) {
        setActive((i) => (i + 1) % STEPS.length);
        startRef.current = now;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, active]);

  const select = useCallback((index) => {
    setActive(index);
    startRef.current = performance.now();
    setProgress(0);
  }, []);

  const step = STEPS[active];

  return (
    <section
      id="tour"
      ref={sectionRef}
      className="grain relative overflow-hidden bg-midnight py-28 text-white"
    >
      {/* Atmosfera */}
      <div className="pointer-events-none absolute inset-0 bg-grid-dark bg-grid [mask-image:radial-gradient(65%_55%_at_50%_40%,#000,transparent)]" />
      <div className="aurora-blob animate-drift" style={{ top: "-10%", left: "5%", width: "40vw", height: "40vw", background: "radial-gradient(circle,rgba(0,178,169,.35),transparent 68%)" }} />
      <div className="aurora-blob animate-drift" style={{ bottom: "-15%", right: "0%", width: "38vw", height: "38vw", background: "radial-gradient(circle,rgba(244,163,64,.28),transparent 68%)", animationDelay: "-11s" }} />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <Reveal>
            <SectionLabel tone="light">Tour pelo sistema</SectionLabel>
          </Reveal>
          <Reveal delay={90}>
            <h2 className="mb-5 mt-6 font-display text-[clamp(2rem,4.4vw,3.25rem)] font-extrabold leading-[1.08]">
              Veja o PawSuite{" "}
              <span className="text-gradient">por dentro</span>
            </h2>
          </Reveal>
          <Reveal delay={170}>
            <p className="text-lg leading-relaxed text-white/55">
              Telas reais do sistema. Clique para explorar cada área — ou deixe
              rolar.
            </p>
          </Reveal>
        </div>

        <div className="grid gap-12 lg:grid-cols-[minmax(0,380px)_1fr] lg:gap-16">
          {/* ---------- Passos ---------- */}
          <Reveal variant="left" className="flex flex-col gap-3">
            {STEPS.map((s, i) => {
              const isActive = i === active;
              return (
                <div
                  key={s.id}
                  className={`group relative overflow-hidden rounded-2xl border transition-all duration-500 ${
                    isActive
                      ? "border-white/15 bg-white/[.07] shadow-2xl"
                      : "border-white/[.06] bg-white/[.02] hover:border-white/12 hover:bg-white/[.05]"
                  }`}
                >
                  {/* Barra de progresso do passo ativo */}
                  {isActive && (
                    <span
                      className="absolute inset-x-0 top-0 z-10 h-[3px] origin-left bg-gradient-to-r from-tealVitality to-pawOrange"
                      style={{ transform: `scaleX(${progress})` }}
                    />
                  )}

                  <button
                    onClick={() => select(i)}
                    aria-expanded={isActive}
                    aria-controls={`tour-panel-${s.id}`}
                    className="flex w-full items-center gap-3 p-5 text-left"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-display text-sm font-bold transition-colors duration-500 ${
                        isActive
                          ? "bg-tealVitality text-white"
                          : "bg-white/10 text-white/45"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span
                      className={`font-display text-lg font-bold transition-colors duration-300 ${
                        isActive
                          ? "text-white"
                          : "text-white/55 group-hover:text-white/80"
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>

                  {/* Conteúdo que expande apenas no passo ativo */}
                  <div
                    id={`tour-panel-${s.id}`}
                    className={`accordion-body ${isActive ? "open" : ""}`}
                  >
                    <div>
                      <div className="px-5 pb-5">
                        <p className="text-sm leading-relaxed text-white/55">
                          {s.text}
                        </p>
                        <ul className="mt-4 space-y-2">
                          {s.bullets.map((b) => (
                            <li
                              key={b}
                              className="flex items-start gap-2.5 text-sm text-white/70"
                            >
                              <svg
                                className="mt-0.5 h-4 w-4 shrink-0 text-tealGlow"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Reveal>

          {/* ---------- Palco ---------- */}
          <Reveal variant="right" delay={140} className="relative">
            <div className="relative">
              <div className="absolute -inset-8 -z-10 rounded-[50px] bg-gradient-to-tr from-tealVitality/25 via-transparent to-pawOrange/20 blur-3xl" />

              <div className="glass-dark relative overflow-hidden rounded-[26px] p-2.5 shadow-[0_50px_100px_-30px_rgba(0,0,0,.7)]">
                {/* Barra da janela */}
                <div className="flex items-center gap-1.5 px-3 pb-2.5 pt-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/25" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/25" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/25" />
                  <span className="ml-3 rounded-md bg-white/[.07] px-2.5 py-1 text-[11px] font-medium text-white/40">
                    app.pawsuite.com.br/{step.id}
                  </span>
                </div>

                {/* Empilha as telas e faz cross-fade — sem flicker de carregamento */}
                <div className="relative overflow-hidden rounded-[18px] bg-white">
                  {STEPS.map((s, i) => (
                    <img
                      key={s.id}
                      src={s.image}
                      alt={s.alt}
                      loading="lazy"
                      className={`w-full transition-all duration-700 ${
                        i === active
                          ? "relative opacity-100 blur-0"
                          : "pointer-events-none absolute inset-0 h-full object-cover object-top opacity-0 blur-md"
                      }`}
                      style={{
                        transitionTimingFunction: "cubic-bezier(.16,1,.3,1)",
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Legenda do passo */}
              <div
                key={step.id}
                className="glass-dark mt-5 rounded-2xl px-6 py-5"
              >
                <h3 className="mb-1.5 font-display text-xl font-bold text-white">
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed text-white/55">
                  {step.text}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ProductTour;
